<template>
	<div class="pain-note-calendar__wrapper">
		<div class="pain-note-calendar">
			<!-- ローディング -->
			<transition name="fade">
				<redraw-progress v-if="isLoading" />
			</transition>
			<!-- /ローディング -->
			<!-- エラー処理 -->
			<transition name="fade">
				<common-error-alert @reload="reload" v-show="isError" />
			</transition>
			<!-- /エラー処理 -->
			<div class="pain-note-calendar__inner" id="js_calendarElem">
				<div class="pain-note-calendar-monthbar">
					<svg @click="exchangeMonth(-1)" class="pain-note-calendar-monthbar__icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
						<path d="M13 15.9999L7 10.0161L13 3.99994" stroke-width="2" />
					</svg>
					{{ monthBarDate }}
					<svg
						@click="exchangeMonth(1)"
						:class="[
							'pain-note-calendar-monthbar__icon',
							{ 'is-disabled': isMonthDisabled }
						]"
						width="16"
						height="14"
						viewBox="0 0 16 14"
						fill="none"
					>
						<path d="M1 1.00006L7 6.98395L1 13.0001" stroke-width="2" />
					</svg>
				</div>
				<ul class="pain-note-calendar-day">
					<li v-for="(dayItem, index) in dayList" class="pain-note-calendar-day__item" :key="index">{{ dayItem }}</li>
				</ul>
				<ul class="pain-note-calendar-date">
					<li v-for="(dateItem, index) in dateList" class="pain-note-calendar-date__item" :class="{'is-disabled': dateItem.disabled, 'is-exist': dateItem.pressure_level}" :key="index" @click="onCalendarClick(dateItem.time)">
						<time :datetime="dateItem.time" class="pain-note-calendar-date__time" :class="{'is-today': dateItem.today, 'is-click': dateItem.click}">{{ dateItem.date }}</time>
						<div :class="[
							'pain-note-calendar-date__record',
							{ 'is-have-drag': dateItem.drug !== 1 }
						]">
							<i :class="`pain-note-calendar-date__level sprite sprite-pressure_level_bg${dateItem.pressure_level}`" :style="dateItem.pressureLevelStyle" />
							<i :class="`pain-note-calendar-date__pain sprite${petClass} sprite${petClass}-pain${dateItem.pain}`" :style="dateItem.painStyle" />
							<i :class="`pain-note-calendar-date__drug sprite sprite-drug${dateItem.drug}`" :style="dateItem.drugStyle" />
							<i :class="`pain-note-calendar-date__menstruation sprite-menstruation calendar_${dateItem.menstruation}`" v-if="dateItem.menstruation" />
						</div>
					</li>
				</ul>
				<div class="pain-note-calendar__button--wrapper">
					<a :class="[
						'pain-note-calendar__button',
						{ 'is-disabled': !calendarButton.unix_time }
					]" :href="calendarButton.unix_time ? `native://openGraph?at=${calendarButton.unix_time}` : 'javascript:void(0)'">
						<i class="pain-note-calendar__button--icon sprite-menstruation pen" /><span class="pain-note-calendar__button--text">{{ calendarButton.text }}</span>
					</a>
				</div>
			</div>

			<!--
			TODO: component化要対応
			以下、PainNoteList.vueを引用
			リスト部分をcomponent化する際に統合する
			-->
			<div class="list" id="js_calendarList">
				<a v-for="painItem in painDataList" :key="`item-${painItem.id}`" class="list-item" :href="`native://openGraph?at=${painItem.unix_time}`">
					<div class="pain-note-list__contents">
						<div class="pain-note-list__contents-left">
							<time :datetime="painItem.time" class="pain-note-list__contents-time"><span class="pain-note-list__contents-time-hour">{{ painItem.hour }}</span>時</time>
							<figure class="pain-note-list__contents-left-bottom">
								<!-- NOTE: level0でも「OK 通常」を表示するよう仕様変更。「.sprite-pressure_level0」で統一化した。 -->
								<i :class="`pain-note-list__contents-left-level-icon sprite sprite-pressure_level${painItem.pressure_level}`" />
								<i :class="`pain-note-list__contents-left-menstruation sprite-menstruation list_${painItem.menstruation}`" v-if="painItem.menstruation" />
							</figure>
						</div>
						<div class="pain-note-list__contents-center">
							<div class="pain-note-list__contents-center-inner">
								<div class="pain-note-list__contents-temperature">
									<span class="pain-note-list__contents-heading">
										<span class="pain-note-list__contents-weather"><i :class="`pain-note-list__contents-weather-icon sprite sprite-weather${painItem.weather_codeicon}`" /></span>
										{{ painItem.weather_codetext }}<br>気温<span class="pain-note-list__contents-unit">(&#8451;)</span>
									</span>
									<span class="pain-note-list__contents-value">{{ painItem.temperature }}</span>
								</div>
								<div class="pain-note-list__contents-pressure">
									<span class="pain-note-list__contents-heading is-wide">気圧<span class="pain-note-list__contents-unit">(hpa)</span><br>{{ painItem.pressure_diffhour }}</span>
									<span class="pain-note-list__contents-value is-wide">{{ painItem.pressure }}<br>{{ painItem.pressure_diffvalue }}</span>
								</div>
							</div>
							<div :class="[
								'pain-note-list__contents-center-inner',
								{ 'is-have-drag': painItem.drug }
							]">
								<div class="pain-note-list__contents-record">
									<i :class="`pain-note-list__contents-pain sprite${petClass} sprite${petClass}-pain${painItem.pain}`" />
									<i :class="`pain-note-list__contents-drug sprite sprite-drug${painItem.drug}`" v-if="painItem.drug" />
								</div>
								<div class="pain-note-list__contents-medicine">
									<p v-if="painItem.medicine_names" class="is-medicine">{{ painItem.medicine_names }}</p>
									<p v-if="painItem.prescription_names" class="is-prescription">{{ painItem.prescription_names }}</p>
									<p v-if="(!painItem.medicine_names && !painItem.prescription_names) && painItem.drug_name">{{ painItem.drug_name }}</p>
								</div>
								<div
									v-show="painItem.comment"
									:class="[
										'pain-note-list__contents-comment',
										`is-${painItem.comment_state}`
									]"
									@click.prevent="painItem.comment_state === 'close' ? painItem.comment_state = 'open' : painItem.comment_state = 'close'">
									<p v-html="painItem.comment"></p>
									<svg class="is-close" width="17" height="11" viewBox="0 0 17 11" fill="none">
										<rect width="17" height="11" rx="4" fill="#999999"/>
										<path d="M11.9531 3.77441L8.51225 7.22456L5.05284 3.77441" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
									</svg>
									<svg class="is-open"  width="17" height="11" viewBox="0 0 17 11" fill="none">
										<rect x="17" y="11" width="17" height="11" rx=" 4" transform="rotate(-180 17 11)" fill="#999999"/>
										<path d="M5.04688 7.22559L8.48775 3.77544L11.9472 7.22559" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
									</svg>
								</div>
							</div>
						</div>
						<div class="pain-note-list__contents-right">
							<svg class="pain-note-list__contents-right_icon" width="20" height="21" viewBox="0 0 20 21" fill="none">
								<path d="M7 4.50006L13 10.4839L7 16.5001" stroke-width="2"/>
							</svg>
						</div>
					</div>
				</a>
			</div>

			<!-- PetVoice ペットのときのみ表示 -->
			<div v-if="isPet" class="pain-note-calendar__info">
				<pet-info :pet-id="petId" :pet-type="petType" :target-date="petTargetDate" :key="petTargetDate" />
			</div>
			<!-- /PetVoice -->
		</div>
	</div>
</template>

<script>

// Vuex
import { mapActions, mapGetters } from 'vuex'
import { SET_WEATHER_DATA } from '@/store/modules/weatherGraph/mutation-types'
import { SET_PAININPERIOD_DATA } from '@/store/modules/painNote/mutation-types'
import { SET_MENSTRUATION_DATA } from '@/store/modules/menstruation/mutation-types'
import { SET_CHIP_DATA } from '@/store/modules/common/mutation-types'

// Compornents
import RedrawProgress from '@/components/Molecules/RedrawProgress'
import CommonErrorAlert from '@/components/Molecules/CommonErrorAlert'
import PetInfo from '@/components/Organisms/PetInfo'

// ライブラリ
// import cmnConst from '@/assets/js/constant.js'
import moment from 'moment'

// NOTE: npmプライベートパッケージ
// https://github.com/pocke-Inc/zutool_chip_parser_web
const ZutoolChipParserWeb = require('@pocke-Inc/zutool_chip_parser_web')

export default {
	name: 'PainNoteCalendarPlus',
	components: {
		RedrawProgress,
		CommonErrorAlert,
		PetInfo
	},

	data () {
		return {
			currentMonth: 0,
			dayList: ['日', '月', '火', '水', '木', '金', '土'],
			monthBarDate: null,
			firstThisMonthUnix: null,
			lastThisMonthUnix: null,
			dateList: [],
			weatherDataList: null,
			pressureLevelList: [],
			painInperiodDataList: null,
			painDrugIconsList: [],
			isLoading: false,
			isError: false,
			isMonthDisabled: false,
			currentIcon: false,
			painDataList: null,
			includeRiseWarning: 0,
			calendarButton: {
				unix_time: null,
				text: null
			},
			isPet: false,
			petId: '',
			petType: '',
			petTargetDate: null,
			petClass: ''
		}
	},

	created () {
		// premiumTypeが10（ファミリーケアプラス）の場合はPetVoice表示・ペットアイコンの判定
		if (this.premiumType.premium_type_code === 10) {
		// if (this.premiumType.premium_type_code !== 10) {	// debug用
			// userData.typeが1（人）でない場合はペットアイコンを有効にする
			this.isPet = this.userData.type !== 1
			// this.isPet = this.userData.type === 1	// debug
			this.petClass = this.isPet ? '-pet' : ''

			// PetVoiceのペットID取得
			this.petId = this.$route.query.pet_id ? this.$route.query.pet_id.toString() : ''

			// PetVoiceのペットタイプ（2: 犬, 3: 猫）取得
			this.petType = this.$route.query.pet_type ? Number(this.$route.query.pet_type) : 0

			// PetVoiceサマリーの表示日設定
			if (this.petId) this.petTargetDate = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).unix()
		}
	},

	mounted () {
		if (!this.dateList.length) {
			this.includeRiseWarning = 1	// 気圧アラート取得のフラグを立てる
			this.initMonthBar(1)	// 次の月を表示するボタンをdisabledにするためcurrentMonthが0の状態で引き数に1を渡す。予報日が次の月にまたがる場合はinitMonthBarメソッド内で判定する。
			this.initDate()
		}
	},

	computed: {
		// map Vuex getters
		...mapGetters('weatherGraph', ['weatherData']),
		...mapGetters('painNote', ['painInperiodData']),
		...mapGetters('menstruation', ['menstruationData']),
		...mapGetters('common', ['userId', 'userToken', 'viewMode', 'userData', 'premiumType', 'chipData'])
	},

	methods: {
		// map Vuex actions
		...mapActions('weatherGraph', [SET_WEATHER_DATA]),
		...mapActions('painNote', [SET_PAININPERIOD_DATA]),
		...mapActions('menstruation', [SET_MENSTRUATION_DATA]),
		...mapActions('common', [SET_CHIP_DATA]),

		/**
		 * 年月バー表示
		 */
		initMonthBar (e) {
			// CHANGED: 当月（currentMonthが0）の場合は翌月(currentMonthが1)のカレンダーを選択可能とする。
			// CHANGED: typeof e === 'object'のケースはエラー画面のリロードボタン（reload関数）やスマパス時代のtabbar切り替えで本関数の引数に何も指定せず呼んでいるため指定している。undefinedで判定するのが正しいので修正。
			// this.isMonthDisabled = (this.currentMonth === 0 && e === 1) || (this.currentMonth === 0 && typeof e === 'object')
			this.isMonthDisabled = (this.currentMonth === 1 && e === 1) || (this.currentMonth === 1 && typeof e === 'object')
			this.monthBarDate = moment(new Date()).add(this.currentMonth, 'month').format('YYYY年MM月')
		},

		/**
		 * カレンダー基礎表示
		 */
		initDate () {
			if (!this.dateList.length) {
				const currentMonth = this.currentMonth
				const firstThisMonthDate = this.dateCal(currentMonth, 1)
				const firstThisMonthObj = moment(firstThisMonthDate)
				const firstThisMonthDay = firstThisMonthObj.day()
				const firstDate = this.dateCal(currentMonth, 1 - firstThisMonthDay)
				this.firstThisMonthUnix = moment(this.dateCal(currentMonth, 1)).unix()
				this.lastThisMonthUnix = moment(this.dateCal(currentMonth + 1, 1)).unix() - 1	// NOTE: 前月1日に記録をつけている且つ、当月1日0時に記録をつけているユーザーが前月のカレンダー表示時にエラーになってしまう問題の修正のため翌月1日0時から当月最終日の23時59分59秒に変更

				// CHANGED: APIの予報日数延長により最大17日先までデータを取得できるようになった。データは取得できるが今回アプリとしては7日間予報から10日間予報への予報延長の対応を行う。なので当月表示（this.currentMonthが0）の場合は10日先までを指定してAPIにリクエストするよう修正する。ただし10日先が翌月に差し掛かるようであれば既存と同じ月末までのリクエストとする（翌月分はAPIレスポンスに含めないため）。
				// CHANGED: 予報が翌月まで差し掛かる場合は翌月のカレンダーを表示できるよう改修。表示月が翌月の場合は翌月に差し掛かる予報までを指定してAPIにリクエスト
				const forecast = moment().add(10, 'days')	// 10日間予報
				if (this.currentMonth === 0 && forecast.month() === moment().month()) {	// 当月表示（this.currentMonthが0）の場合は10日先までを指定してAPIにリクエスト
					this.lastThisMonthUnix = forecast.unix()
				} else if (this.currentMonth === 1) {	// 翌月表示（this.currentMonthが1）の場合は翌月に差し掛かる予報先までを指定してAPIにリクエスト、差し掛からない場合はfromに1時間足してリクエスト（最大17日先までデータが取れてしまうので1日とか足すと意図しない気圧アイコンが表示されてしまう。21時までのデータが取れていないとアイコン表示しない仕様のため1時間とした）
					const diff = forecast.diff(firstThisMonthObj)
					this.lastThisMonthUnix = diff <= 0 ? firstThisMonthObj.add(1, 'hours').unix() : firstThisMonthObj.add(diff).unix()
				}
				// console.log('firstThisMonthUnix: ', moment.unix(this.firstThisMonthUnix).format('YYYY/MM/DD HH:mm:ss'))	// NOTE: Debug用
				// console.log('lastThisMonthUnix: ', moment.unix(this.lastThisMonthUnix).format('YYYY/MM/DD HH:mm:ss'))	// NOTE: Debug用

				// NOTE: 6 => 0から数えた曜日数
				const lastThisMonthDate = this.dateCal(currentMonth + 1, 0)
				const lastThisMonthDay = moment(lastThisMonthDate).day()
				const lastDate = this.dateCal(currentMonth + 1, 6 - lastThisMonthDay)

				// NOTE: 86400 => 1日を秒数に変換した値
				const dateLen = (moment(lastDate).unix() - moment(firstDate).unix()) / 86400 + 1

				// カレンダーのボタン初期化
				this.setCalendarButton(null)

				for (let i = 0; i < dateLen; i++) {
					this.dateList.push({
						time: moment(this.dateCal(currentMonth, 1 - firstThisMonthDay + i)).format('YYYY-MM-DD'),
						date: moment(this.dateCal(currentMonth, 1 - firstThisMonthDay + i)).date(),
						disabled: moment(this.dateCal(currentMonth, 1 - firstThisMonthDay + i)).month() !== moment(new Date()).add(this.currentMonth, 'month').month(),
						today: moment(new Date()).format('YYYY-MM-DD') === moment(this.dateCal(currentMonth, 1 - firstThisMonthDay + i)).format('YYYY-MM-DD'),
						pressure_level: 0,
						pain: 0,
						drug: 1,
						menstruation: null,
						pressureLevelStyle: '',
						painStyle: '',
						drugStyle: ''
					})
				}

				this.setWeatherData()
			}
		},

		/**
		 * 気象データ提供API
		 */
		setWeatherData () {
			let pressureLevelHour = null
			this.isLoading = true
			this.SET_WEATHER_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				from: this.firstThisMonthUnix,
				to: this.lastThisMonthUnix,
				include_rise_warning: this.includeRiseWarning
			}).then(res => {
				// NOTE: 3, 9, 15, 21 => 4つの時間帯のデータを取得する
				this.weatherDataList = this.weatherData.records.filter(v => {
					pressureLevelHour = moment.unix(v.hour).hour()
					return pressureLevelHour === 3 || pressureLevelHour === 9 || pressureLevelHour === 15 || pressureLevelHour === 21
				}).map(item => {
					return {
						'time': moment.unix(item.hour),
						'hour': moment.unix(item.hour).hour(),
						'pressure_level': item.pressure_level
					}
				})

				// CHANGE: いったんすべてスクロール許可にするためコメントアウト
				// this.dataListHeight()
				this.pressureLevelDate()
			}).catch(error => {
				// エラー処理
				console.log(error)
				this.isError = true
			})
		},

		/**
		 * 気圧レベルリスト生成
		 */
		pressureLevelDate () {
			const riseWarningRanking = 2.5 // NOTE: 上昇注意表示の優先順位のための数値（やや注意＜上昇注意＜注意）
			const riseWarningApiData = 6 // NOTE: 上昇注意のAPI仕様の数値
			let pressureLevelData = null // NOTE: 3-21時の6時間毎の気圧レベル
			let pressureLevelMax = 1 // NOTE: 日毎の最大の気圧レベル

			for (let i = 0, len = this.weatherDataList.length; i < len; i++) {
				// NOTE: 日毎のpressureLevelMaxを最小値1にしてリセットする
				if (i % 4 === 0) pressureLevelMax = 1
				pressureLevelData = this.weatherDataList[i].pressure_level === riseWarningApiData ? riseWarningRanking : this.weatherDataList[i].pressure_level
				if (pressureLevelMax < pressureLevelData) pressureLevelMax = pressureLevelData

				// NOTE: i % 4 === 3 => 21時まで取得できた日のみ生成
				if (i % 4 === 3) {
					this.pressureLevelList.push({
						pressure_level: pressureLevelMax === riseWarningRanking ? riseWarningApiData : pressureLevelMax
					})
				}
			}

			this.setPressureLevel()
		},

		/**
		 * カレンダー基礎表示リストに気圧レベルパラメータを追加
		 */
		setPressureLevel () {
			const currentMonth = this.currentMonth
			const firstThisMonthDate = this.dateCal(currentMonth, 1)
			const firstThisMonthDay = moment(firstThisMonthDate).day()
			const firstDate = this.dateCal(currentMonth, 1 - firstThisMonthDay)
			const firstDateDiff = moment(firstThisMonthDate - firstDate).date() - 1
			const lastDate = moment(this.dateCal(currentMonth + 1, 0)).date()
			const lastDateDiff = lastDate - this.pressureLevelList.length

			// NOTE: if => 1日目が存在する場合
			for (let i = 0, len = this.pressureLevelList.length; i < len; i++) {
				if (moment(this.weatherDataList[0].time).date() === 1) {
					this.dateList[i + firstDateDiff].pressure_level = this.pressureLevelList[i].pressure_level
				} else {
					this.dateList[i + firstDateDiff + lastDateDiff].pressure_level = this.pressureLevelList[i].pressure_level
				}
			}

			this.getPainInperiodData()
		},

		/**
		 * 記録データ取得(期間)API
		 */
		getPainInperiodData () {
			// NOTE: 痛み記録データ取得(期間)
			const SET_PAININPERIOD_DATA = this.SET_PAININPERIOD_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				from: this.firstThisMonthUnix,
				to: this.lastThisMonthUnix,
				include_rise_warning: this.includeRiseWarning
			})

			// NOTE: チップデータ取得
			const SET_CHIP_DATA = this.SET_CHIP_DATA({
				type: 99	// 1: 人, 99: 動物 NOTE: 現状は動物のみ
			})

			const apiArray = [SET_PAININPERIOD_DATA, SET_CHIP_DATA]
			const isFemale = this.userData && (this.userData.sex === '2' || this.userData.sex === 2)
			// NOTE: 女性であれば生理日記録の処理をする
			if (isFemale) {
				// NOTE: 生理日記録を月またぎを考慮してカレント月の前月の初分から次月の月初＋30日（最大整理期間）まで取得
				// NOTE: APIはfromは開始日、toは終了日をみているようです。
				const lastMonth = moment(this.dateCal(this.currentMonth - 1, 1)).unix()
				const nextMonth = moment(this.dateCal(this.currentMonth + 1, 1)).add(30, 'days').unix()
				const SET_MENSTRUATION_DATA = this.SET_MENSTRUATION_DATA({
					user_id: this.userId,
					user_token: this.userToken,
					from: lastMonth,
					to: nextMonth
				})
				apiArray.push(SET_MENSTRUATION_DATA)
			}

			// API通信
			Promise.all(apiArray).then(res => {
				this.painInperiodDataList = this.painInperiodData.records.map(item => {
					return {
						'date': moment.unix(item.hour.sec).date(),
						'pain': Number(item.pain),
						'drug': Number(item.drug)
					}
				})

				if (this.menstruationData && this.menstruationData.records.length !== 0) this.setMenstruationData()	// 生理日記録があれば処理
				this.painDrugIconsData()
			}).catch(error => {
				// TODO: エラー処理
				console.log(error)
				this.isError = true
			}).finally(() => {
				this.isLoading = false
			})
		},

		/**
		 * 生理日データの作成
		 * TODO: 排卵日、PMSはリリース時なしだが入れる前提。入れる場合、このメソッド内で処理する。
		 */
		setMenstruationData () {
			const menstruationData = this.menstruationData.records
			const menstruationList = []
			const isEnabled = this.userData && this.userData.menstruation && (this.userData.menstruation.enabled === '1' || this.userData.menstruation.enabled === 1)

			// 生理日記録の履歴を加工する
			for (let i = 0, len = menstruationData.length; i < len; i++) {
				const item = menstruationData[i]
				const startDate = moment.unix(item.from_time.sec)
				const endDate = moment.unix(item.to_time.sec)
				// NOTE: termは秒数で返ってくるよう変更になるため、開始日と終了日の差分をとるよう変更した
				// const term = Number(item.term)
				const term = endDate.diff(startDate, 'days')
				const expected = item.is_expected ? '_scheduled' : ''
				const currentMonth = moment(this.dateCal(this.currentMonth, 1)).format('M')

				// NOTE: 生理記録が無効あれば生理予定日をデータに含めない(次のループへ)
				if (!isEnabled && expected !== '') continue

				// from、toから日付のリストを作る
				if (currentMonth === startDate.format('M')) {
					menstruationList.push({
						'date': startDate,
						'type': `start${expected}`
					})
				}
				for (let i = 1, len = term; i < len; i++) {
					const cloneDate = startDate.clone()
					const duringDate = cloneDate.add(i, 'd')
					if (currentMonth === duringDate.format('M')) {
						menstruationList.push({
							'date': duringDate,
							'type': `during${expected}`
						})
					}
				}
				if (currentMonth === endDate.format('M')) {
					menstruationList.push({
						'date': endDate,
						'type': `end${expected}`
					})
				}
			}

			// カレンダー基礎表示リストに生理日パラメータ追加
			for (let i = 0, len = this.dateList.length; i < len; i++) {
				const index = menstruationList.findIndex(item => item.date.format('YYYY-MM-DD') === this.dateList[i].time)
				if (index > -1) this.dateList[i].menstruation = menstruationList[index].type
			}
		},

		/**
		 * 痛み・薬アイコンリスト生成
		 */
		painDrugIconsData () {
			/**
			 * 日付が重複したデータを以下のルールでマージし、新たな配列を生成
			 * 痛みアイコン：レベルが一番高いもの(1～4)
			 * 薬アイコン：複数服薬(4) > 処方薬(3) > 市販薬(2) > なし(1)
			 */
			const painInperiodDataList = this.painInperiodDataList
			let dateData = null
			let painData = 0
			let drugData = 0
			let existDateList = []

			/**
			 * if  : 特定の日付の1つ目のデータ
			 * else: 特定の日付の2つ目以降のデータ（マージ）
			 */
			// CHANGED: 薬アイコンの表示順をカウンタで実装していたが、数字が高いものを優先とする仕様なので痛みレベルの実装に合わせた。
			for (let i = 0, len = painInperiodDataList.length; i < len; i++) {
				if (dateData !== painInperiodDataList[i].date) {
					dateData = painInperiodDataList[i].date
					painData = painInperiodDataList[i].pain
					drugData = painInperiodDataList[i].drug
					existDateList.push(dateData)
					this.painDrugIconsList.push(painInperiodDataList[i])
				} else {
					painData = painInperiodDataList[i].pain > painData ? painInperiodDataList[i].pain : painData
					drugData = painInperiodDataList[i].drug > drugData ? painInperiodDataList[i].drug : drugData
					this.painDrugIconsList[this.painDrugIconsList.length - 1].pain = painData
					this.painDrugIconsList[this.painDrugIconsList.length - 1].drug = drugData
				}
			}

			/**
			 * 1ヶ月分のデータを生成するため、記録がついていない日付のデータを空で生成
			 */
			const currentMonth = this.currentMonth
			const lastDate = moment(this.dateCal(currentMonth + 1, 0)).date()
			let emptyDateList = []
			let emptyIconsList = []

			for (let i = 0; i < lastDate; i++) {
				emptyDateList.push(i + 1)
			}

			emptyDateList = emptyDateList.filter(val => {
				return existDateList.indexOf(val) === -1
			})

			for (let i = 0, len = emptyDateList.length; i < len; i++) {
				emptyIconsList.push({
					'date': emptyDateList[i],
					'pain': 0,
					'drug': 1
				})
			}

			/**
			 * 記録がついている日付のデータと記録がついていない日付のデータをマージ
			 */
			this.painDrugIconsList = this.painDrugIconsList.concat(emptyIconsList)

			this.painDrugIconsList.sort((a, b) => {
				return a.date < b.date ? -1 : 1
			})

			this.setPainDrugIcons()
		},

		/**
		 * カレンダー基礎表示リストに痛み・薬アイコンパラメータ追加
		 */
		setPainDrugIcons () {
			const currentMonth = this.currentMonth
			const firstThisMonthDate = this.dateCal(currentMonth, 1)
			const firstThisMonthDay = moment(firstThisMonthDate).day()
			const firstDate = this.dateCal(currentMonth, 1 - firstThisMonthDay)
			const firstDateDiff = moment(firstThisMonthDate - firstDate).date() - 1
			// NOTE: 700 => 1行(7日分 * 100)
			const squaresWidth = window.innerWidth / 700

			for (let i = 0, len = this.dateList.length; i < len; i++) {
				if (this.painDrugIconsList[i]) {
					const drug = this.painDrugIconsList[i].drug
					const indexDataList = i + firstDateDiff
					this.dateList[indexDataList].pain = this.painDrugIconsList[i].pain
					this.dateList[indexDataList].drug = drug
					// NOTE: 0.8, 0.64, 0.5, 0.4 => 決め打ちの拡大率
					// CHANGED: pressureLevelStyle、painStyle、drugStyleをdata属性で持ち共通で使用していたが、アイコン毎に調整できるようdateList配列のオブジェクトメンバーで持つよう修正
					this.dateList[indexDataList].pressureLevelStyle = `transform:scale(calc(0.8 * ${squaresWidth})) translate(-50%, -50%);`
					this.dateList[indexDataList].painStyle = `transform:scale(calc(0.64 * ${squaresWidth})) translate(-50%, -50%);`
					this.dateList[indexDataList].drugStyle = drug !== 4 ? `transform:scale(calc(0.5 * ${squaresWidth})) translate(-50%, -50%);` : `transform:scale(calc(0.4 * ${squaresWidth})) translate(-50%, -50%);`
				}
			}
		},

		/**
		 * 月初月末の日付や曜日設定
		 */
		dateCal (mValue, dValue) {
			return new Date(new Date().getFullYear(), new Date().getMonth() + mValue, dValue)
		},

		/**
		 * API読み込み前に保存していたデータ群を空にする
		 */
		dataReset () {
			this.dateList.length = 0
			this.pressureLevelList.length = 0
			this.painDrugIconsList.length = 0
			this.painDataList = null
		},

		/**
		 * 表示月の前後月を表示
		 */
		exchangeMonth (e) {
			if (!this.isLoading) {
				this.dataReset()
				this.currentMonth += e
				this.initMonthBar(e)
				this.initDate()
			}
		},

		/**
		 * カレンダークリックイベント
		 */
		onCalendarClick (e) {
			const target = moment(e)
			// 未来日なら何もしない
			if (target.isBefore(moment().set({ hour: 23, minute: 59, second: 59, milliseconds: 999 }))) {
				this.petTargetDate = target.unix()
				this.getPainData(e)
			}
		},

		/**
		 * リロード処理
		 */
		reload () {
			this.isError = false
			this.dataReset()
			this.initMonthBar()
			this.initDate()
		},

		/**
		 * ----------------------------------------------------------------
		 *
		 * TODO: component化要対応
		 * 以下、PainNoteList.vueを引用
		 * リスト部分をcomponent化する際に統合する
		 *
		 * ----------------------------------------------------------------
		 */

		/**
		 * 記録データ取得(期間)API
		 */
		getPainData (e) {
			// NOTE: カレンダー下のボタン設定
			this.setCalendarButton(e)

			this.SET_PAININPERIOD_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				from: moment(e).unix(),
				to: moment(e).add(1, 'day').unix() - 1,	// NOTE: 翌日の0時が表示される問題の修正のため、翌日0時から当日の23時59分59秒に変更
				include_rise_warning: this.includeRiseWarning
			}).then(res => {
				this.painDataList = this.painInperiodData.records.map(item => {
					// NOTE: 生理日記録の処理
					let menstruation = null
					const menstruationData = this.menstruationData ? this.menstruationData.records : []
					for (let i = 0, len = menstruationData.length; i < len; i++) {
						const data = menstruationData[i]
						switch (true) {
							// NOTE: 予定日はリストに表示させない（痛み登録と生理記録は必ず同じタイミングで登録されるのでこのケースは生理記録機能導入時のみ考えられる）
							// NOTE: 生理日記録の開始日と終了日は同じ日時（YYYYMMDDHH）で比較する（unix_timeそのものの比較だとsecまで同じでないとイコールにならないため）
							case data.is_expected:
								break
							case moment.unix(data.from_time.sec).isSame(moment.unix(item.hour.sec), 'hour'):
								menstruation = 'start'
								break
							case moment.unix(data.to_time.sec).isSame(moment.unix(item.hour.sec), 'hour'):
								menstruation = 'end'
								break
							case data.from_time.sec < item.hour.sec && data.to_time.sec > item.hour.sec:
								menstruation = 'during'
								break
						}
					}
					// NOTE: 複数服薬での変更内容
					// drug_namesからmedicinesを参照するよう変更し、複数の薬を表示できるようにした。旧バージョンとの互換性を保つためmedicinesがない場合はdrug_namesを表示する。
					// 市販薬・処方薬のアイコンをつけ分けてあいうえお順にソートして表示する（ない場合は今まで通り表示しない）。
					// 「その他のくすり」は例外で一番最後にする。ID（other_otc=市販薬・other_prescription=処方薬）をみて判定する
					const medicines = item.medicines ? item.medicines : null
					const drugName = medicines	// 旧バージョン用の表示
						? medicines.map(medicine => medicine.name).join()
						: item.drug_names && item.drug_names[0] !== 'null'
							? item.drug_names[0]
							: ''
					const getMedicineString = (medicines, type) => {	// 市販薬・処方薬を分けてあいうえお順でソートした文字列を返すローカル関数
						const otherMedicineID = type === 0 ? 'other_otc' : 'other_prescription'
						return medicines ? medicines
							.filter(medicine => Number(medicine.prescription_flag) === type)
							.sort((a, b) => {
								if (a.id === otherMedicineID) {	// aが「その他のくすり」ならaをbの後ろに並べる
									return 1
								} else if (b.id === otherMedicineID) {	// bが「その他のくすり」ならaをbの前に並べる
									return -1
								} else {	// 「その他のくすり」以外は通常の判定
									return a.name_hira < b.name_hira ? -1 : a.name_hira > b.name_hira ? 1 : 0
								}
							})
							.map(medicine => medicine.name).join()
							: ''
					}
					const medicineNames = getMedicineString(medicines, 0)	// 市販薬
					const prescriptionNames = getMedicineString(medicines, 1)	// 処方薬
					return {
						'id': item._id,
						'time': moment.unix(item.hour.sec).format('YYYY-MM-DDTHH:mm'),
						'unix_time': item.hour.sec,	// ネイティブアプリのhref値につけるエポックタイム(秒)
						'hour': moment.unix(item.hour.sec).hour(),
						'pressure_level': item.weather.pressure_level,
						'weather_codeicon': item.weather.weather_code,
						'weather_codetext': this.weatherCodetextItem(item.weather.weather_code),
						'temperature': item.weather.temperature,
						'pressure': item.weather.pressure,
						'pressure_diffhour': this.pressureDiffhourItem(moment.unix(item.hour.sec).hour()),
						'pressure_diffvalue': this.pressureDiffvalueItem(item.weather.pressure_diff),
						'pain': item.pain,
						'drug': this.drugItem(item.drug),
						'drug_name': drugName,
						'medicine_names': medicineNames,
						'prescription_names': prescriptionNames,
						'comment': item.comment ? this.convertChipComment(item.comment) : '',
						'comment_state': 'hidden',	// コメントの開閉状態の初期値
						'menstruation': menstruation
					}
				})

				this.clickDataNumber(e)

				this.$nextTick(() => {
					this.addCommentClass()
				})
			}).catch(error => {
				// エラー時はなにもしない
				console.log(error)
			})
		},

		/**
		 * 天気表示
		 */
		weatherCodetextItem (e) {
			e = Number(e)
			return e === 1 ? '晴れ' : e === 2 ? 'くもり' : e === 3 ? '雨' : '雪'
		},

		/**
		 * 気圧差の時間帯表示
		 */
		pressureDiffhourItem (e) {
			return e >= 3 && e < 9 ? '03-09' : e >= 9 && e < 15 ? '09-15' : e >= 15 && e < 21 ? '15-21' : '21-03'
		},

		/**
		 * 気圧差の値表示
		 */
		pressureDiffvalueItem (e) {
			return e > 0 ? `+${e}` : e
		},

		/**
		 * 薬アイコン表示
		 */
		drugItem (e) {
			e = Number(e)
			return e === 1 ? null : e
		},

		/**
		 * クリックした日付に色付与
		 */
		clickDataNumber (e) {
			for (let i = 0, dateLen = this.dateList.length; i < dateLen; i++) {
				this.dateList[i].click = this.dateList[i].time === e
			}
		},

		/**
		 * カレンダー下のボタン初期化処理
		 */
		setCalendarButton (e) {
			if (e) {
				let targetTime = moment(e).hour(12).minute(0).second(0)	// 選択した日の12時
				const now = moment().minute(0).second(0)

				// NOTE: 選択した日が今日の場合、12時である必要はなさそうなので現在時刻とする。選択した日が未来の日ならunix_timeをnullにする。
				this.calendarButton.text = `${moment(targetTime).format('M月D日')}のグラフに移動して記録する`
				this.calendarButton.unix_time = now.isSame(targetTime, 'day') ? now.unix() : now.isAfter(targetTime, 'day') ? targetTime.unix() : null
				// console.log(this.calendarButton.unix_time)
			} else {
				this.calendarButton.text = 'グラフに移動して記録する'
				this.calendarButton.unix_time = null
			}
		},

		/**
		 * コメントのチップ置換処理
		 */
		convertChipComment (comment) {
			// XSS攻撃対応で「<」と「>」をエスケープして、改行コードを<br>タグに変換してからパース処理
			const result = ZutoolChipParserWeb.ChipParser.parse(comment.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>'), this.chipData.records)
			return result.map(item => {
				if (item.type === 0) {
					return item.text
				} else {
					const record = this.chipData.records.find(chip => chip.name === item.text)
					return `<em class="is-chip is-type0${record.category}">${item.text}</em>`
				}
			}).join('')
		},

		/**
		 * コメント開閉クラス付与処理
		 */
		addCommentClass () {
			const elements = document.getElementsByClassName('pain-note-list__contents-comment')
			for (let i = 0; i < elements.length; i++) {
				const element = elements[i]
				this.$set(this.painDataList[i], 'comment_state', element.offsetHeight > 92 ? 'close' : 'hidden')
			}
		}
	},

	beforeDestroy () {
	}
}
</script>

<style lang="scss" scoped>
@import "@/assets/sass/variable";

.pain-note-calendar {
	$pressure-level-bg-icon-size: 56; // 気圧レベル実サイズ
	$pressure-level-bg-icon-origin: 106; // 気圧レベル元サイズ
	background: $background-secondary;
	height: auto;

	.list {
		max-height: 100%;
	}
	.list-item {
		padding: 0;
		overflow: hidden;
		border-bottom: 1px solid $background-list;
		text-decoration: none;
	}
	&-monthbar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-family: $family-primary;
		background: $_white;
		padding: $spacing-10 0;

		& > svg {
			cursor: pointer;
			padding: 0 $spacing-20;

			path { stroke: $calendar-month-primary-icon; }

			&.is-disabled {
				cursor: none;
				pointer-events: none;

				path { stroke: $calendar-month-secondary-icon; }
			}
		}
	}
	&-day {
		display: flex;
		font-family: $family-primary;
		background: $_white;
		margin: 0;
		padding: 0;
		list-style: none;
		&__item {
			color: $calendar-date-text;
			font-size: $font-size-10;
			width: calc(100vw / 7);
			padding: 0 $spacing-6;
		}
	}
	&-date {
		display: flex;
		flex-wrap: wrap;
		font-family: $family-primary;
		background: $_white;
		border-bottom: 1px solid $background-list;
		margin: 0;
		padding: 0;
		list-style: none;
		&__item {
			position: relative;
			color: $calendar-day-primary-text;
			font-size: $font-size-10;
			border-top: 1px solid $background-list;
			width: calc(100vw / 7);
			height: calc(100vw / 7);
			pointer-events: none;
			&.is-disabled {
				.pain-note-calendar-date__time {
					color: $calendar-day-secondary-text;
				}
			}
			&.is-exist {
				pointer-events: auto;
			}
		}
		&__time {
			position: absolute;
			top: 2px;
			left: 0;
			width: 15px;
			height: 15px;
			text-align: center;
			border-radius: 50%;
			pointer-events: none;
			z-index: 1;
			&.is-today {
				color: $text-seconday;
				background: $calendar-today;
			}
			&.is-click {
				color: $text-seconday;
				background: $calendar-active;
			}
			&.is-today.is-click {
				background: $calendar-active;
			}
		}
		&__record {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			pointer-events: none;

			&.is-have-drag .pain-note-calendar-date__pain { margin-top: $spacing-8; }
		}
		&__level {
			position: absolute;
			top: 50%;
			left: 50%;
			width: #{$pressure-level-bg-icon-size}px;
			height: #{$pressure-level-bg-icon-size}px;
			transform: scale(0) translate(-50%, -50%);
			transform-origin: 0 0;
		}
		&__pain {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: scale(0) translate(-50%, -50%);
			transform-origin: 0 0;
		}
		&__drug {
			position: absolute;
			top: 50%;
			left: 50%;
			margin-top: -#{$spacing-6};
			transform: scale(0) translate(-50%, -50%);
			transform-origin: 0 0;
		}
		&__menstruation {
			position: absolute;
			transform: scale(.55);	// NOTE: 縮尺率はおおよその値
			transform-origin: 100% 0;

			&.calendar_start,
			&.calendar_start_scheduled {
				top: $spacing-2;
				right: $spacing-2;
			}
			&.calendar_during,
			&.calendar_during_scheduled {
				top: $spacing-8;
				right: $spacing-6;
			}
			&.calendar_end,
			&.calendar_end_scheduled {
				top: $spacing-2;
				right: $spacing-10;
			}
		}
	}

	&__button {
		position: relative;
		display: block;
		width: 265px;
		margin: 0 auto;
		padding: $spacing-4 $spacing-4 $spacing-4 $spacing-10;
		box-sizing: border-box;
		background-color: $button-primary;
		color: $text-seconday;
		font-size: $font-size-12;
		text-decoration: none;
		line-height: $line-height-1;
		border-radius: 20px;

		&--wrapper {
			padding: $spacing-2 0 $spacing-6;
			background-color: $background-primary;
		}

		&--icon {
			position: absolute;
			top: $spacing-4;
			left: $spacing-4;
			transform: scale(.5);
			transform-origin: 0 0;
		}

		&--text {
			display: block;
			width: 100%;
			padding: $spacing-10 $spacing-4 $spacing-10 $spacing-20;
			box-sizing: border-box;
			text-align: center;
		}

		&.is-disabled::before {
			content: '';
			position: absolute;
			display: block;
			width: 100%;
			height: 100%;
			top: 0;
			left: 0;
			background-color: rgba($_black, .4);
			border-radius: 20px;
			z-index: 10;
		}
	}
}

/**
 * ----------------------------------------------------------------
 *
 * TODO: component化要対応
 * 以下、PainNoteList.vueを引用
 * リスト部分をcomponent化する際に統合する
 * また、元のデザインと若干表示が異なるので余裕があるときにリファクタリングしたい
 *
 * ----------------------------------------------------------------
 */
.pain-note-list {
	$left-size: 45; // 左カラムサイズ
	$right-size: 20; // 右カラムサイズ
	$record-size: 38; // 痛み・薬アイコンカラムサイズ
	$pressure-level-icon-size: 45; // 気圧レベル実サイズ
	$pressure-level-icon-origin: 180; // 気圧レベル元サイズ
	$weather-icon-size: 24; // 天気アイコン実サイズ
	$weather-icon-origin: 69; // 天気アイコン元サイズ
	$pain-icon-size: 32; // 痛みアイコン実サイズ
	$pain-icon-origin: 99; // 痛みアイコン元サイズ
	$drug-icon-size: 32; // 薬アイコン実サイズ
	$drug-icon-origin: 99; // 薬アイコン元サイズ
	$drug-icon-multi-size: 20; // 複数服薬アイコン実サイズ。
	$spacing-44: 2.75rem;
	$spacing-92: 5.75rem;
	$color-chip-state: #E26B5C;
	$color-chip-record: #EB9C38;
	$color-chip-care: #83B75F;

	&__contents {
		display: flex;
		width: 100%;
		padding: $spacing-8;
		box-sizing: border-box;
		&-left {
			position: relative;
			width: #{$left-size}px;
		}
		&-time {
			display: block;
			font-size: $font-size-12;
			height: 28px;
			margin-top: -#{$spacing-8};
			line-height: 28px;
		}
		&-time-hour {
			font-size: $font-size-20;
			font-family: $family-primary;
		}
		&-left-bottom {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			width: 100%;
			height: #{$left-size}px;
			margin: $spacing-8 0 0;
		}
		&-left-level-icon {
			position: absolute;
			transform: scale(calc(#{$pressure-level-icon-size} / #{$pressure-level-icon-origin}));
		}
		&-left-menstruation {
			$menstruation-icon-scale: .6;	// NOTE: 縮尺率はおおよその値
			position: absolute;
			transform: scale($menstruation-icon-scale);

			&.list_start {
				transform-origin: 100% 100%;
				bottom: -#{$spacing-4};
				right: -#{$spacing-10};
			}
			&.list_during {
				transform-origin: 100% 0;
				top: calc(50% + 2px);
				transform: scale($menstruation-icon-scale) translateY(-50%);	// NOTE: 縮尺率はおおよその値
				right: -#{$spacing-4};
			}
			&.list_end {
				transform-origin: 100% 0;
				top: $spacing-4;
				right: -#{$spacing-10};
			}
		}
		&-center {
			box-sizing: border-box;
			width: calc(100% - #{$left-size + $right-size}px);
			padding: 0 $spacing-4 0 $spacing-10;
		}
		&-center-inner {
			display: flex;
			flex-wrap: wrap;
			width: 100%;
			min-height: $spacing-40;
			box-sizing: border-box;
			& + & { margin-top: $spacing-10; }
			// NOTE: 薬情報を持っている場合のレイアウト
			&.is-have-drag {
				.pain-note-list__contents-pain { top: $spacing-10; }
			}
		}
		&-temperature {
			display: flex;
			align-items: center;
			box-sizing: border-box;
			border-left: 5px solid $background-weather;
			width: 100px;
			line-height: 1.4;
			padding-left: $spacing-2;
		}
		&-weather {
			width: #{$weather-icon-size}px;
			height: #{$weather-icon-size}px;
			margin-top: -#{$spacing-6};
			float: left;
		}
		&-weather-icon {
			transform: scale(calc(#{$weather-icon-size} / #{$weather-icon-origin}));
			transform-origin: 0 0;
		}
		&-pressure {
			display: flex;
			align-items: center;
			border-left: 5px solid $background-pressure-note;
			line-height: 1.3;
			padding-left: $spacing-2;
		}
		&-heading {
			white-space: nowrap;
			width: 50px;
			&.is-wide {
				width: 60px;
			}
		}
		&-unit {
			font-size: $font-size-10;
		}
		&-value {
			color: $text-bold;
			font-size: $font-size-14;
			font-family: $family-primary;
			padding-top: $spacing-18;
			&.is-wide {
				padding-top: 0;
			}
		}
		&-record {
			position: relative;
			box-sizing: border-box;
			width: #{$record-size}px;
			min-height: $spacing-44;
			padding-left: $spacing-2;
			border-left: 5px solid $background-pain;
		}
		&-pain {
			position: absolute;
			top: $spacing-6;
			left: 0;
			width: #{$pain-icon-size}px;
			height: #{$pain-icon-size}px;
			transform: scale(calc(#{$pain-icon-size} / #{$pain-icon-origin}));
			transform-origin: 0 0;
		}
		&-drug {
			position: absolute;
			top: -#{$spacing-4};
			left: 0;
			width: #{$drug-icon-size}px;
			height: #{$drug-icon-size}px;
			transform: scale(calc(#{$drug-icon-size} / #{$drug-icon-origin}));
			transform-origin: 0 0;

			&.sprite-drug4 {
				top: $spacing-2;
				left: $spacing-6;
				transform: scale(calc(#{$drug-icon-multi-size} / #{$drug-icon-origin}));
			}
		}
		&-medicine {
			font-size: $font-size-9;
			font-family: $family-secondary;
			width: calc(100% - #{$record-size}px);
			line-height: $line-height;
			word-break: break-all;
			text-align: justify;
			// NOTE: 薬名記録対応で追加
			p {
				margin: 0;
				padding-top: $spacing-4;
				box-sizing: border-box;
				&.is-empty::before { content: none; }

				// NOTE: 複数服薬で追加（変数はデザインシステム外）
				$spacing-3: 0.187rem;
				$color-medicine-tag: #FC4951;
				$color-prescription-tag: #1172D8;

				&.is-medicine::before {
					content: '市販薬';
					background-color: $color-medicine-tag;
				}
				&.is-prescription::before {
					content: '処方薬';
					background-color: $color-prescription-tag;
				}
				&.is-medicine::before,
				&.is-prescription::before  {
					font-family: $lp-family-primary;
					display: inline-block;
					text-align: center;
					margin: 0 $spacing-2 0 0;
					padding: 0 $spacing-3;
					color: $text-seconday;
					font-weight: normal;
					border-radius: 3px;
				}
			}
		}
		&-comment {
			position: relative;
			width: 100%;
			margin-top: $spacing-8;
			padding: $spacing-8 $spacing-30 $spacing-8 $spacing-8;
			font-size: $font-size-10;
			font-family: $family-secondary;
			line-height: $line-height;
			border-radius: $spacing-4;
			background-color: $background-secondary;
			box-sizing: border-box;
			overflow-wrap: break-word;

			& > p {
				width: 100%;
				margin: 0;
				text-align: justify;
			}
			& > svg {
				position: absolute;
				top: 50%;
				right: $spacing-8;
				transform: translateY(-50%);
			}
			// チップ
			/deep/ .is-chip {
				display: inline-block;
				padding: $spacing-4 $spacing-8;
				margin: 0 $spacing-4 $spacing-4;
				color: $text-seconday;
				background-color: $color-chip-state;	// デフォルトは様子
				font-style: normal;
				border-radius: $spacing-4;
				line-height: $line-height-1;

				&.is-type01 { background-color: $color-chip-state; }	// 様子
				&.is-type02 { background-color: $color-chip-record; }	// 記録
				&.is-type03 { background-color: $color-chip-care; }	// お世話
				&:first-child { margin: 0 $spacing-4 $spacing-4 0; }
			}
			/deep/ br + .is-chip { margin: $spacing-4 $spacing-4 $spacing-4 0; }

			// コメント開閉
			&.is-hidden {
				pointer-events: none;
				& > svg { display: none; }
			}
			&.is-close {
				max-height: $spacing-92;
				overflow: hidden;
				&::after {
					content: '';
					display: block;
					position: absolute;
					bottom: -#{$spacing-2};
					left: 0;
					width: 100%;
					height: $spacing-20;
					background: linear-gradient(to bottom, rgba($background-secondary, 0) 0%, rgba($background-secondary, 1) 50%, rgba($background-secondary, 1) 100%);
				}
				& > svg.is-close { display: block; }
				& > svg.is-open { display: none; }
			}
			&.is-open {
				max-height: none;
				& > svg.is-open { display: block; }
				& > svg.is-close { display: none; }
			}
		}
		&-right {
			width: #{$right-size}px;
			display: inline-flex;
			justify-content: center;
			align-items: center;

			&_icon path {
				width: 100%;
				stroke: $list-chevron-icon;
			}
		}
	}
}
.sprite {
	// background-image: url(~@/assets/img/pain_sprite_icon.png);
	background-image: url(~@/assets/img/sprite_pain_icon.png);	// こちらのスプライト画像を正とする
	background-repeat: no-repeat;
	display: block;
}

.sprite-drug1 {
	display: none;
}

.sprite-drug2 {
	width: 99px;
	height: 99px;
	background-position: 0 0;
}

.sprite-drug3 {
	width: 99px;
	height: 99px;
	background-position: 0 -99px;
}

.sprite-pain0 {
	display: none;
}

.sprite-pain1 {
	width: 99px;
	height: 99px;
	background-position: 0 -198px;
}

.sprite-pain2 {
	width: 99px;
	height: 99px;
	background-position: 0 -297px;
}

.sprite-pain3 {
	width: 99px;
	height: 99px;
	background-position: 0 -396px;
}

.sprite-pain4 {
	width: 99px;
	height: 99px;
	background-position: 0 -495px;
}

.sprite-pressure_level0 {
	width: 180px;
	height: 180px;
	background-position: 0 -774px;
}

.sprite-pressure_level1 {
	width: 180px;
	height: 180px;
	background-position: 0 -774px;
}

.sprite-pressure_level2 {
	width: 180px;
	height: 180px;
	background-position: 0 -954px;
}

.sprite-pressure_level3 {
	width: 180px;
	height: 180px;
	background-position: 0 -1134px;
}

.sprite-pressure_level4 {
	width: 180px;
	height: 180px;
	background-position: 0 -1314px;
}

.sprite-pressure_level_bg0 {
	display: none;
}

.sprite-pressure_level_bg1 {
	width: 106px;
	height: 105px;
	background-position: 0 -1494px;
}

.sprite-pressure_level_bg2 {
	width: 106px;
	height: 105px;
	background-position: 0 -1599px;
}

.sprite-pressure_level_bg3 {
	width: 106px;
	height: 105px;
	background-position: 0 -1704px;
}

.sprite-pressure_level_bg4 {
	width: 106px;
	height: 105px;
	background-position: 0 -1809px;
}

.sprite-weather1 {
	width: 69px;
	height: 69px;
	background-position: 0 -1914px;
}

.sprite-weather2 {
	width: 69px;
	height: 69px;
	background-position: 0 -1983px;
}

.sprite-weather3 {
	width: 69px;
	height: 69px;
	background-position: 0 -2052px;
}

.sprite-weather4 {
	width: 69px;
	height: 69px;
	background-position: 0 -2121px;
}

// 上昇アラート追加
.sprite-pressure_level6 {
	width: 180px;
	height: 180px;
	background-position: 0 -2190px;
}

.sprite-pressure_level_bg6 {
	width: 106px;
	height: 105px;
	background-position: 0 -2370px;
}

// 複数服薬アイコン追加
.sprite-drug4 {
	width: 99px;
	height: 99px;
	background-position: 0 -2475px;
}

// ペットアイコンのスプライト
.sprite-pet {
	background-image: url(~@/assets/img/pet/sprite_pet_pain.png);
	background-repeat: no-repeat;
	display: block;

	&-pain0 {
		display: none;
	}

	&-pain1 {
		width: 99px;
		height: 99px;
		background-position: -5px -5px;
	}

	&-pain2 {
		width: 99px;
		height: 99px;
		background-position: -5px -114px;
	}

	&-pain3 {
		width: 99px;
		height: 99px;
		background-position: -5px -223px;
	}

	&-pain4 {
		width: 99px;
		height: 99px;
		background-position: -5px -332px;
	}
}

</style>
